import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * useVideoEditor — owns all editor state + every save/generate/trim handler.
 * The modal shell just wires UI parts to this hook.
 */
export function useVideoEditor(post, { onClose, onSaved }) {
  const videoRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [startSec, setStartSec] = useState(post.video_start_sec || 0);
  const [endSec, setEndSec] = useState(post.video_end_sec || 0);
  const [captions, setCaptions] = useState(post.captions || "");
  const [coverUrl, setCoverUrl] = useState(post.cover_image_url || "");
  const [genCap, setGenCap] = useState(false);
  const [genCover, setGenCover] = useState(false);
  const [saving, setSaving] = useState(false);
  const [trimming, setTrimming] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onMeta = () => {
      const d = v.duration || 0;
      setDuration(d);
      if (!endSec) setEndSec(d);
    };
    v.addEventListener("loadedmetadata", onMeta);
    return () => v.removeEventListener("loadedmetadata", onMeta);
  }, [endSec]);

  const previewSegment = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = startSec;
    v.play();
    const stop = () => {
      if (v.currentTime >= endSec) {
        v.pause();
        v.removeEventListener("timeupdate", stop);
      }
    };
    v.addEventListener("timeupdate", stop);
  };

  const generateCaptions = async () => {
    setGenCap(true);
    try {
      const r = await api.post("/ai/captions", { title: post.content, style: "snappy" });
      setCaptions(r.data.captions);
      toast.success("Caption generated ✦");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caption gen failed");
    }
    setGenCap(false);
  };

  const generateCover = async () => {
    setGenCover(true);
    try {
      const r = await api.post("/ai/cover", { prompt: post.content || "cinematic creator content" });
      setCoverUrl(r.data.cover_image_url);
      toast.success("Cover generated ✦");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cover gen failed");
    }
    setGenCover(false);
  };

  const persistEdits = async () => {
    return api.post(`/posts/${post.id}/video-edit`, {
      video_start_sec: parseFloat(startSec) || 0,
      video_end_sec: parseFloat(endSec) || duration,
      captions: captions || null,
      cover_image_url: coverUrl || null,
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await persistEdits();
      onSaved?.(updated.data);
      toast.success("Saved");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const renderTrim = async () => {
    setTrimming(true);
    try {
      await persistEdits();
      const r = await api.post(`/posts/${post.id}/video-trim`);
      onSaved?.(r.data);
      toast.success("Video re-encoded ✦");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trim failed");
    }
    setTrimming(false);
  };

  return {
    videoRef, duration, startSec, setStartSec, endSec, setEndSec,
    captions, setCaptions, coverUrl,
    genCap, genCover, saving, trimming,
    previewSegment, generateCaptions, generateCover, save, renderTrim,
  };
}

export const fmtTime = (s) => {
  if (!s && s !== 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};
